import { useEffect, useMemo, useState } from 'react'

/**
 * QueueTabs — generic 3-tab queue used by Lab / Radiology / Pharmacy /
 * Ambulance dashboards.
 *
 * Bins each row into one of three buckets based on the caller's
 * `classifyRow(row) → 'new' | 'processed' | 'archived'`. The component
 * renders the tab strip with count badges, remembers the last-active tab
 * across rerenders (within a session — not persisted), and renders an
 * empty state when the active bucket is empty.
 *
 * Why a shared component instead of repeating tab logic in each
 * dashboard? Four reasons:
 *   1. Status taxonomy is per-domain (lab uses "completed", pharmacy
 *      uses "dispensed", ambulance uses different states again). The
 *      classifier lives in the dashboard where that vocabulary belongs.
 *      The tab UX is identical across all four — no reason to fork it.
 *   2. Dashboards stay short — only their domain logic, no UI noise.
 *   3. Adding a 4th tab later (e.g. "Cancelled" or "Critical") is one
 *      edit here, not four.
 *   4. Visual consistency for the staff agent: every queue page across
 *      every role uses the same tab bar in the same place.
 *
 * Required props:
 *   rows           — Array of rows to bin and render.
 *   classifyRow    — fn(row) returning 'new' | 'processed' | 'archived'.
 *   renderRow      — fn(row, idx) returning the React node for one row.
 *   keyExtractor   — fn(row) returning a unique key per row.
 *
 * Optional props:
 *   emptyMessages  — { new, processed, archived } string overrides for
 *                    empty-bucket messaging. Defaults are role-agnostic.
 *   defaultTab     — Which tab to open on mount. Defaults to 'new' (the
 *                    one with action items).
 *   tabLabels      — { new, processed, archived } label overrides — e.g.
 *                    "New Prescriptions" / "Processed Prescriptions" if
 *                    the parent wants verb-flavoured labels.
 */
export default function QueueTabs({
  rows = [],
  classifyRow,
  renderRow,
  keyExtractor,
  emptyMessages,
  defaultTab = 'new',
  tabLabels,
}) {
  const [tab, setTab] = useState(defaultTab)

  const buckets = useMemo(() => {
    const out = { new: [], processed: [], archived: [] }
    for (const row of rows) {
      const bin = classifyRow(row) || 'new'
      // Defensive — if the classifier returns an unknown bin, drop the
      // row into 'new' so it can't disappear silently.
      if (out[bin]) out[bin].push(row)
      else out.new.push(row)
    }
    return out
  }, [rows, classifyRow])

  // If the agent was on, say, "Archived" and a tabs-flush refresh pushes
  // them to a row count of 0 there but >0 in "New", they'd see an empty
  // state by default. Auto-fall-through to 'new' ONCE on mount when the
  // chosen default tab is empty so the first load surfaces the most
  // useful bucket. After that, respect the user's tab choice.
  useEffect(() => {
    if (buckets[defaultTab]?.length === 0 && buckets.new.length > 0 && tab === defaultTab) {
      // No-op — the user is on the configured default tab; don't yank
      // them off it just because it's empty. Empty state is fine.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const labels = {
    new: 'New',
    processed: 'Processed',
    archived: 'Archived',
    ...(tabLabels || {}),
  }
  const empties = {
    new: 'No new items waiting for action.',
    processed: 'Nothing processed in the last 30 days.',
    archived: 'No older items in the archive.',
    ...(emptyMessages || {}),
  }

  const visibleRows = buckets[tab] || []

  return (
    <>
      {/* Tab strip — same visual language as the doctor dashboard's
          status pills so the staff agent's eye finds it instantly.
          Pill style (rather than underline-bottom) because these tabs
          filter buckets within one queue, not navigate between
          sections. */}
      <div className="flex items-stretch gap-2 mb-3 -mx-1 overflow-x-auto">
        {(['new', 'processed', 'archived']).map((key) => {
          const isActive = tab === key
          const count = buckets[key].length
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={
                'inline-flex items-center gap-2 rounded-md px-5 py-2 text-base tracking-wide border-2 transition ' +
                (isActive
                  ? 'bg-wantok-700 border-wantok-700 text-white font-bold'
                  : 'bg-white border-slate-200 text-slate-700 font-semibold hover:border-wantok-400 hover:text-wantok-800')
              }
              aria-pressed={isActive}
            >
              <span>{labels[key]}</span>
              <span
                className={
                  'inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded-full px-2 py-0 text-xs font-bold ' +
                  (isActive
                    ? 'bg-white/25 text-white'
                    : count > 0
                      ? 'bg-wantok-50 text-wantok-700 border border-wantok-100'
                      : 'bg-slate-100 text-slate-400')
                }
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Active bucket */}
      <ul className="divide-y divide-slate-100">
        {visibleRows.length === 0 ? (
          <li className="py-4 text-sm text-slate-400 italic">
            {empties[tab]}
          </li>
        ) : (
          visibleRows.map((row, idx) => (
            <Row key={keyExtractor ? keyExtractor(row) : idx} render={renderRow} row={row} idx={idx} />
          ))
        )}
      </ul>
    </>
  )
}

// Tiny wrapper so callers can pass `renderRow={(row) => <li>...</li>}` OR
// `renderRow={(row) => <SomeComponent row={row} />}` without us having to
// care which is which.
function Row({ render, row, idx }) {
  return render(row, idx)
}

// ---------------------------------------------------------------------------
// Shared classifier helper.
//
// Every dashboard's classifier collapses down to "if the order is in a
// terminal/done state and was created within the cutoff, processed;
// older terminal → archived; everything else → new". The set of "done"
// statuses differs per domain so each caller passes their own.
// ---------------------------------------------------------------------------
const ARCHIVE_AFTER_DAYS = 30

/**
 * Build a classifier closure for a domain that uses `created_at` and a
 * caller-supplied set of "done" status strings.
 *
 *   const classify = makeClassifier({
 *     doneStatuses: ['completed', 'ready'],
 *     statusField: 'status',           // optional, default 'status'
 *     dateField: 'created_at',         // optional
 *     archiveAfterDays: 30,            // optional
 *   })
 *
 * Returns: (row) → 'new' | 'processed' | 'archived'
 */
export function makeClassifier({
  doneStatuses,
  statusField = 'status',
  dateField = 'created_at',
  archiveAfterDays = ARCHIVE_AFTER_DAYS,
}) {
  const done = new Set((doneStatuses || []).map((s) => String(s).toLowerCase()))
  const cutoffMs = archiveAfterDays * 24 * 60 * 60 * 1000
  return function classify(row) {
    const status = String(row?.[statusField] || '').toLowerCase()
    if (!done.has(status)) return 'new'
    const ts = row?.[dateField] ? new Date(row[dateField]).getTime() : NaN
    if (!Number.isFinite(ts)) return 'processed'
    return Date.now() - ts <= cutoffMs ? 'processed' : 'archived'
  }
}
